import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import { CheckCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { getAsana } from 'services/asana/getAsana';
import { completeAsana } from 'services/asana/completeAsana';
import { sendReport } from 'services/report/sendReport';
import PranayamaList from './PranayamaList';
import { getPranayammas } from 'services/pranayamma/getPranayammas';
import { getRelaxations } from 'services/relaxation/getRelaxation'; // New service import
import RelaxationList from './RelaxationList';
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`
  };
}

function StandalonePractice() {
  const { courseid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const studentid = localStorage.getItem('studentid');
  const parsedStudentId = studentid ? Number(studentid) : null;
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedPose, setSelectedPose] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 = Asanas, 1 = Pranayamas, 2 = Relaxation
  const hasProcessedRef = useRef(new Set());

  // ------------------ Asanas ------------------
  const { data, isLoading, error, refetch } = useQuery({
    queryFn: () => getAsana({ courseId: Number(courseid), studentId: parsedStudentId }),
    queryKey: ['asanas', courseid, parsedStudentId],
    enabled: !!parsedStudentId
  });

  const yogaAsanas = (data?.response || []).sort((a, b) => a.posturenumber - b.posturenumber);

  // ------------------ Pranayamas ------------------
  const {
    data: pranayamaData,
    isLoading: pranayamaLoading,
    error: pranayamaError
  } = useQuery({
    queryFn: () => getPranayammas({ courseid: Number(courseid) }),
    queryKey: ['pranayammas', courseid]
  });
  const pranayamas = pranayamaData?.data || [];

  // ------------------ Relaxation ------------------
  const {
    data: relaxationData,
    isLoading: relaxationLoading,
    error: relaxationError
  } = useQuery({
    queryKey: ['relaxation', courseid, parsedStudentId],
    queryFn: () => getRelaxations({ courseid: Number(courseid), studentid: parsedStudentId }),
    enabled: !!courseid && !!parsedStudentId
  });
  const relaxations = relaxationData?.data || [];

  // ------------------ Mutations ------------------
  const sendReportMutation = useMutation({
    mutationFn: sendReport,
    onSuccess: (data) => {
      if (data?.status) toast.success('Report created successfully');
      else toast.error(data?.message || 'Failed to create report');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error.message || 'Error sending report');
    }
  });

  const completeAsanaMutation = useMutation({
    mutationFn: completeAsana,
    onSuccess: () => {
      toast.success('Asana marked as completed');
      queryClient.invalidateQueries(['asanas', Number(courseid), parsedStudentId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to mark asana as completed');
    }
  });

  // ------------------ Handle Tab from navigation ------------------
  useEffect(() => {
    const isContinuous = location.state?.sequenceType === 'continuous';

    if (location.state?.activeTab !== undefined) {
      setTabValue(location.state.activeTab);
    } else if (isContinuous) {
      setTabValue(1); // Pranayama
    } else {
      setTabValue(0); // Asanas for standalone
    }
  }, [location.state]);

  // ------------------ Process session after pose tracking ------------------
  useEffect(() => {
    if (!location.state?.completed || !location.state?.sessionData) return;

    const sessionData = Array.isArray(location.state.sessionData) ? location.state.sessionData[0] : location.state.sessionData;
    const sessionKey = `${location.state.id}_${sessionData.completedAt || Date.now()}`;
    if (hasProcessedRef.current.has(sessionKey)) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (!sessionData || completeAsanaMutation.isLoading || sendReportMutation.isLoading) return;

    hasProcessedRef.current.add(sessionKey);
    navigate(location.pathname, { replace: true, state: {} });

    (async () => {
      try {
        const isValid =
          sessionData.poseId &&
          Number.isFinite(Number(sessionData.averageAccuracy)) &&
          Number(sessionData.averageAccuracy) > 0 &&
          Number.isFinite(Number(sessionData.captureDuration)) &&
          Number(sessionData.captureDuration) > 0 &&
          Number.isFinite(Number(sessionData.frameCount)) &&
          Number(sessionData.frameCount) > 0 &&
          Number.isFinite(Number(sessionData.goodPostureTime)) &&
          Number(sessionData.goodPostureTime) > 0 &&
          sessionData.studentLandmarks;

        if (!isValid) {
          toast.error('Invalid session data');
          return;
        }

        const completeAsanaResponse = await completeAsanaMutation.mutateAsync({
          studentId: parsedStudentId,
          courseId: Number(courseid),
          postureId: Number(location.state.id),
          asanaimage: sessionData.asanaimage,
          studentLandmarks: sessionData.studentLandmarks
        });

        await sendReportMutation.mutateAsync({
          studentId: parsedStudentId,
          averageAccuracy: sessionData.averageAccuracy,
          captureDuration: sessionData.captureDuration,
          frameCount: sessionData.frameCount,
          goodPostureTime: sessionData.goodPostureTime,
          courseId: Number(courseid),
          poseId: Number(location.state.id),
          asanaimage: completeAsanaResponse.asanaimage || sessionData.asanaimage,
          studentLandmarks: sessionData.studentLandmarks
        });
      } catch {
        toast.error('Failed to process asana');
      }
    })();
  }, [location.state, completeAsanaMutation.isLoading, sendReportMutation.isLoading, parsedStudentId, courseid]);

  // ------------------ Check login ------------------
  if (!parsedStudentId) {
    return (
      <Box sx={{ padding: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Please log in to continue
        </Typography>
        <Button variant="contained" color="primary" onClick={() => navigate('/login')} sx={{ marginTop: 2 }}>
          Go to Login
        </Button>
      </Box>
    );
  }

  // ------------------ Common variables ------------------
  const courseName = yogaAsanas[0]?.course_name || 'Practice Standalone Asanas';
  const completedCount = yogaAsanas.filter((pose) => pose.completed === 1).length;
  const totalCount = yogaAsanas.length;
  const completionPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const getProgressColor = (value) => {
    if (value < 40) return '#e53935';
    if (value < 75) return '#fb8c00';
    return '#43a047';
  };

  // ------------------ Handle Start Tracking ------------------
  const handleStartTracking = (item) => {
    console.log('Selected Item:', item);

    if (!item) return;

    // ✅ RELAXATION (CHECK FIRST)
    if (item.relaxation_type || item.audio_file || item.video_file) {
      navigate('/relaxation-tracking', {
        state: {
          courseId: Number(courseid),
          selectedId: item.id,
          name: item.track_name,
          audio: item.audio_file,
          video: item.video_file
        }
      });
      return;
    }

    // ✅ PRANAYAMA
    if (item.breathing_sequence) {
      navigate('/pranayama-tracking', {
        state: {
          courseId: Number(courseid),
          selectedId: item.id,
          name: item.name,
          breathing_sequence: item.breathing_sequence,
          loops: item.loops || 1,
          instructions: item.instructions || ''
        }
      });
      return;
    }

    // ✅ ASANA (POSE)
    if (item.posturename) {
      navigate('/pose-tracking', {
        state: {
          courseId: Number(courseid),
          studentId: parsedStudentId,
          sequenceType: 'standalone',
          selectedPoseId: item.id,
          courseName,
          posturename: item.posturename
        }
      });
    }
  };

  const handleConfirmRepractice = () => {
    if (selectedPose) {
      navigate('/pose-tracking', { state: { ...selectedPose, courseId: courseid, courseName } });
    }
    setOpenConfirmDialog(false);
    setSelectedPose(null);
  };

  // ------------------ Render ------------------
  if (isLoading) return <Typography textAlign="center">Loading...</Typography>;

  return (
    <Box sx={{ padding: 0 }}>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} centered textColor="primary" indicatorColor="primary">
          <Tab label="Asanas" {...a11yProps(0)} />
          <Tab label="Pranayamas" {...a11yProps(1)} />

          {/* Only show Relaxation tab for standalone */}
          {location.state?.sequenceType !== 'continuous' && <Tab label="Relaxation" {...a11yProps(2)} />}
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* Asanas */}
        <Typography variant="h4" gutterBottom textAlign="center">
          {courseName}
        </Typography>
        <Box sx={{ maxWidth: 400, margin: 'auto', marginBottom: 4 }}>
          <Typography variant="subtitle1" textAlign="center" sx={{ paddingBottom: 2 }}>
            Progress: {completedCount}/{totalCount} ({completionPercentage.toFixed(0)}%)
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{
                height: 20,
                borderRadius: 2,
                [`& .MuiLinearProgress-bar`]: {
                  backgroundColor: getProgressColor(completionPercentage)
                }
              }}
            />
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 600
              }}
            >
              {completionPercentage.toFixed(0)}%
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {yogaAsanas.map((pose) => (
            <Grid item xs={12} sm={6} md={4} key={pose.id}>
              <Card>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {pose.asanaImage ? (
                    <img
                      src={pose.asanaImage}
                      alt={pose.posturename}
                      style={{ width: '100%', height: 'auto' }}
                      onError={(e) => (e.target.src = '/fallback-image.png')}
                    />
                  ) : pose.hasVideo ? (
                    <video
                      src={pose.mediaUrl}
                      style={{ width: '100%', height: 'auto', maxHeight: 200 }}
                      controls
                      playsInline
                      preload="metadata"
                      onError={(e) => console.error('Video error:', e.target.error?.message)}
                    />
                  ) : (
                    <Box sx={{ width: '100%', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        No Media Available
                      </Typography>
                    </Box>
                  )}
                </Box>

                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ flexGrow: 1, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {pose.posturename}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: pose.completed === 1 ? 'success.main' : 'error.main' }}>
                      {pose.completed === 1 ? 'Completed' : 'Not Started'}
                    </Typography>
                  </Box>
                  <Divider />
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    disabled={!pose.asanaImage && !pose.hasVideo}
                    variant="contained"
                    color="primary"
                    startIcon={pose.completed === 1 ? <CheckCircleOutlined /> : <PlayCircleOutlined />}
                    onClick={() => handleStartTracking(pose)}
                  >
                    {pose.completed === 1 ? 'Repractice' : 'Start Tracking'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Pranayamas */}
        <PranayamaList pranayamas={pranayamas} onStartTracking={(pran) => handleStartTracking(pran)} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {location.state?.sequenceType === 'continuous' ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
            <SpaOutlined sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Relaxation is built into your flow
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
              In Continuous Flow mode, relaxation happens naturally at the end of your practice (usually a guided Savasana or short
              wind-down). No separate sessions are needed here.
            </Typography>
          </Box>
        ) : relaxationLoading ? (
          <Typography textAlign="center">Loading relaxation exercises...</Typography>
        ) : relaxationError ? (
          <Typography color="error" textAlign="center">
            {relaxationError.message}
          </Typography>
        ) : relaxations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10, px: 3 }}>{/* your existing empty state */}</Box>
        ) : (
          <RelaxationList relaxations={relaxations} onStartTracking={handleStartTracking} />
        )}
      </TabPanel>

      {/* Re-practice Dialog */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Repractice Asana</DialogTitle>
        <DialogContent>
          <Typography>You have already completed {selectedPose?.posturename}. Would you like to practice it again?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRepractice} color="primary" variant="contained">
            Repractice
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StandalonePractice;
